import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

import { PageNames } from 'src/app/configs/page-names.enum';
import {
  ChildrenPrivateNumberBuyRequestsPaginationDto,
  CustomResultOfPaginatedListOfChildrenPrivateNumberBuyRequestsPaginationDto,
  GetEnumValuesDto,
  PaginatedListOfChildrenPrivateNumberBuyRequestsPaginationDto,
  PrivateNumberBuyRequestStatus,
  SmsCp,
} from 'src/app/core/api/models';
import { PrivateNumberBuyRequestService } from 'src/app/core/api/services';
import { EventTypes } from 'src/app/core/constants/event-types';
import { FormPageConfig } from 'src/app/core/models/form-page-config.model';
import { AppDomService } from 'src/app/core/services/dom.service';
import { MenuBtnComponent } from 'src/app/shared/global/menu-btn/menu-btn.component';
import { QueryStringTypesEnum } from '../../../configs/query-string-types';
import { BaseIndexPageComponent } from '../../shared/base/components/base-index-page.component';
import { BaseIndexModule } from '../../shared/base/modules/base-index.module';
import { PrivateNumberDocumentationModule } from '../private-number-documentation/private-number-documentation.module';
import { PrivateNumberBuyRequestStatusDirective } from '../shared/private-number-buy-request-status.directive';
import { RefreshMenuService } from './../../../core/services/refresh-menu.service';
import { UpdateStatusPrivateNumberPriceComponent } from './update-status-private-number-price/update-status-private-number-price.component';

@Component({
  selector: 'app-children-private-number-buy-requests',
  templateUrl: './children-private-number-buy-requests.component.html',
  imports: [
    MenuBtnComponent,
    BaseIndexModule,
    PrivateNumberDocumentationModule,
    PrivateNumberBuyRequestStatusDirective,
  ],
})
export class ChildrenPrivateNumberBuyRequestsComponent
  extends BaseIndexPageComponent
  implements OnInit
{
  //#region properties
  pageName = PageNames.childrenPrivateNumberBuyRequests;
  title = 'pages.childrenPrivateNumberBuyRequests.title';

  protected currentService = inject(PrivateNumberBuyRequestService);
  createGetApiMethodName: string =
    this.currentService.privateNumberBuyRequestCreateGet.name;
  auditApiMethodName: string =
    this.currentService
      .privateNumberBuyRequestGetPrivateNumberPricesAuditWithPagination.name;

  modelWithPagination$: Observable<PaginatedListOfChildrenPrivateNumberBuyRequestsPaginationDto>;
  items$: Observable<Array<ChildrenPrivateNumberBuyRequestsPaginationDto>>;
  formPageConfig: FormPageConfig<any> = {};
  PrivateNumberBuyRequestStatusEnumValues: GetEnumValuesDto[] = [];
  SmsCpEnumValues: GetEnumValuesDto[] = [];
  showDocuments: boolean = false;
  requestId: any;
  childOrganizationId: number;
  DocumentationTypeEnumValues: GetEnumValuesDto[] = [];

  get PrivateNumberBuyRequestStatus() {
    return PrivateNumberBuyRequestStatus;
  }

  //#endregion

  constructor(
    public refreshMenuService: RefreshMenuService,
    public cd: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit() {
    try {
      super.ngOnInit();
      this.pageConfig.showAddBtn = false;
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.privateNumberPrice
      );
    }
  }

  setPageAfterEnterUrl() {
    super.setPageAfterEnterUrl();
    let params = this.routingState.parseQueryString();
    for (const [key, value] of Object.entries(params)) {
      if (key == QueryStringTypesEnum.Documents_key)
        this.onShowChildPage({
          values: [...(value as string)?.split('_')],
          setUrl: false,
          queryStringKey: key,
          class: this,
          showPagePropKey: this.componentProperties.showDocuments,
          selectedPropKey: [
            {
              keyName: this.componentProperties.requestId,
              keyType: typeof this.requestId,
            },
            {
              keyName: this.componentProperties.childOrganizationId,
              keyType: typeof this.childOrganizationId,
            },
          ],
        });
    }
  }

  getEnumsValues() {
    this.getEnumValuesApi('PrivateNumberBuyRequestStatus')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        (values) => (this.PrivateNumberBuyRequestStatusEnumValues = values)
      );
    this.getEnumValuesApi('SmsCp')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((values) => (this.SmsCpEnumValues = values));
    this.getEnumValuesApi('PrivateNumberPurchaseDocuments')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((values) => (this.DocumentationTypeEnumValues = values));
  }

  configTableColumns(): void {
    this.tableColumns = [
      {
        title: this.localizer.title,
        name: 'PrivateNumberPriceTitle',
        hasSearch: true,
        wrap: true,
      },
      {
        title: this.localizer.number,
        name: 'number',
        hasSearch: true,
        wrap: true,
        dir: 'ltr',
      },
      {
        title: this.localizer.organization,
        name: 'organizationName',
        hasSearch: true,
        wrap: true,
      },
      {
        title: `${this.tableVocabs}payAmount`,
        name: 'payAmount',
        hasSearch: true,
        needCurrency: true,
        type: 'number',
        wrap: true,
      },
      {
        title: `${this.tableVocabs}privateNumberPriceNumberOfDigits`,
        name: 'privateNumberPriceNumberOfDigits',
        hasSearch: true,
        type: 'number',
        wrap: true,
      },
      {
        title: `${this.tableVocabs}privateNumberPriceIsRound`,
        name: 'privateNumberPriceIsRound',
        type: 'boolean',
        hasSearch: true,
        wrap: true,
      },
      {
        title: `common.smsCp`,
        name: 'PrivateNumberPriceSmsCp',
        hasSearch: true,
        type: 'enum',
        enumName: 'SmsCp',
        wrap: true,
      },
      {
        title: `${this.tableVocabs}isService`,
        name: 'isService',
        type: 'boolean',
        hasSearch: true,
        wrap: true,
      },
      {
        title: this.localizer.status,
        name: 'PrivateNumberBuyRequestStatus',
        hasSearch: true,
        type: 'enum',
        enumName: 'PrivateNumberBuyRequestStatus',
        wrap: true,
      },
      {
        title: this.localizer.description,
        name: 'rejectionDescription',
        hasSearch: true,
        wrap: true,
      },
      {
        title: `${this.tableVocabs}assignmentDate`,
        name: 'assignmentDate',
        hasSearch: true,
        type: 'dateTime',
        wrap: true,
      },
      {
        title: `${this.tableVocabs}reservationDate`,
        name: 'reservationDate',
        hasSearch: true,
        type: 'dateTime',
        wrap: true,
      },
      {
        title: `${this.tableVocabs}cancellationDate`,
        name: 'cancellationDate',
        hasSearch: true,
        type: 'dateTime',
        wrap: true,
      },
    ];
  }

  //#region api

  //get All
  getPaginationApi(): Observable<CustomResultOfPaginatedListOfChildrenPrivateNumberBuyRequestsPaginationDto> {
    this.clearFiltersAndSetDefaultSort();
    return this.currentService.privateNumberBuyRequestGetChildrenPrivateNumberBuyRequestsPagination(
      {
        body: this.getBaseModelWithPaginationParams(),
      }
    );
  }

  setDefaultTableSort() {
    try {
      this.sortObject.push({ columnName: 'reservationDate', sort: 1 });
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.privateNumberPrice
      );
    }
  }

  //Delete
  baseDeleteApi(id: number) {
    this.unSubs();

    this.pageConfig.loading = true;
    this.lastSelectedItemId = id;

    this.currentService
      .privateNumberBuyRequestDelete({
        body: { id: id },
      })
      .pipe(this.basePipeOperators())
      .subscribe(this.baseDeleteObserver);
  }

  //#endregion
  onShowDocuments(id, childOrganizationId, setUrl = true) {
    this.showParent = false;
    this.setParentQueryString();
    if (setUrl) {
      this.location.go(
        window.location.pathname +
          '?' +
          QueryStringTypesEnum.Documents_key +
          '=' +
          id
      );
    }
    try {
      this.requestId = id;
      this.childOrganizationId = childOrganizationId;
      this.showDocuments = true;
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.privateNumberPrice
      );
    }
  }

  onUpdateStatus(
    id: number,
    status: PrivateNumberBuyRequestStatus,
    smsCp: SmsCp
  ) {
    try {
      this.unSubs();

      const modalResult = this.dialogService.openModal({
        component: UpdateStatusPrivateNumberPriceComponent,
        data: {
          id: id,
          pageVocabs: this.pageVocabs,
          status: status,
          smsCp: smsCp,
        },
      });

      modalResult
        .afterClosed()
        .pipe(
          takeUntil(this.onDestroy$),
          tap(() => {
            AppDomService.removeModalScrollClassFromHtml();
          })
        )
        .subscribe((res) => {
          if (res) {
            this.getTableData();
            this.cd.detectChanges();
          }
        });
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.privateNumberPrice
      );
    }
  }
}
